package horcrux.frontend.common.views.collections.navigator

import horcrux.common.HasBetterToString
import horcrux.common.api.dtos.types.HasID
import horcrux.common.utils.safelet.safeLet
import horcrux.frontend.common.GlobalAppState
import horcrux.frontend.common.views.collections.MetadataByID
import js.objects.jso
import kotlinx.browser.document
import mui.material.Autocomplete
import mui.material.AutocompleteProps
import mui.material.FormControl
import mui.material.FormControlVariant
import mui.material.TextField
import mui.system.PropsWithSx
import mui.system.sx
import react.FC
import react.Props
import react.ReactNode
import react.create
import react.dom.html.ReactHTML.div
import react.useEffect
import web.cssom.Color
import web.cssom.JustifyContent
import web.html.HTMLDivElement

external interface CollectionSelectorProps<ID : HasBetterToString, Metadata : HasID<ID>, MetadataByIDImpl> :
    PropsWithSx {
    var globalAppState: GlobalAppState
    var metadataByID: MetadataByID<ID, Metadata, MetadataByIDImpl>
    var componentLabelBase: String
    var currentMetadataMaybe: Metadata?
    var labelSupplier: (Metadata) -> String
    var onChange: (String) -> Unit
}

private external interface AutoCompleteOptionData {
    var label: String
    var id: String
}

private val AutoCompleteOptionData.collectionOptionID: String
    get() = "collection-option-$id"

private fun AutoCompleteOptionData(label: String, id: String): AutoCompleteOptionData = jso {
    this.label = label
    this.id = id
}

private external interface AutoCompleteOptionProps : Props {
    var autoCompleteOptionData: AutoCompleteOptionData
    var currentOptionIDMaybe: String?
    var renderOptionProps: react.dom.html.HTMLAttributes<web.html.HTMLLIElement>

}

private val AutoCompleteOption = FC<AutoCompleteOptionProps> { props ->
    // Scroll selected option into view when this component is mounted
    safeLet(props.currentOptionIDMaybe) { currentOptionID ->
        if (currentOptionID == props.autoCompleteOptionData.id) {
            useEffect {
                (document.querySelector("#${props.autoCompleteOptionData.collectionOptionID}") as? HTMLDivElement)?.let {
                    it.scrollIntoView()
                }
            }
        }
    }
    div {
        +props.renderOptionProps
        +props.autoCompleteOptionData.label
        this.id = props.autoCompleteOptionData.collectionOptionID
        this.style = jso {
            this.justifyContent = JustifyContent.flexStart
            // Selected option will be highlighted with a dark background
            if (props.currentOptionIDMaybe == props.autoCompleteOptionData.id) {
                this.backgroundColor = Color("#555555")
                this.color = Color("#fff")
            }
        }
    }
}

fun <ID : HasBetterToString, Metadata : HasID<ID>, MetadataByIDImpl> collectionSelector(): FC<CollectionSelectorProps<ID, Metadata, MetadataByIDImpl>> =
    FC { props ->
        FormControl {
            this.fullWidth = true
            this.sx { +props.sx }
            Autocomplete<AutocompleteProps<AutoCompleteOptionData>> {
                this.id = "select-${props.componentLabelBase}"
                props.currentMetadataMaybe?.let {
                    this.value = AutoCompleteOptionData(props.labelSupplier(it), it.id.betterToString())
                }
                this.options = props.metadataByID.values.map {
                    AutoCompleteOptionData(
                        props.labelSupplier(it), it.id.betterToString()
                    )
                }.sortedBy { it.label }.toTypedArray()
                this.getOptionKey = { it.id }
                this.getOptionLabel = { it.label }
                this.renderOption = { renderOptionProps, optionData, _, _ ->
                    AutoCompleteOption.create {
                        this.autoCompleteOptionData = optionData
                        this.currentOptionIDMaybe = props.currentMetadataMaybe?.id?.betterToString()
                        this.renderOptionProps = renderOptionProps
                    }
                }
                this.renderInput = { params ->
                    TextField.create {
                        +params
                        this.label = ReactNode("Select a ${props.componentLabelBase}")
                        this.variant = FormControlVariant.outlined
                    }
                }
                this.onChange =
                    { _, _, _, autocompleteChangeDetails -> autocompleteChangeDetails?.let { props.onChange(it.option.id) } }
            }
        }
    }
